import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { AlertController } from '@ionic/angular';
import { UnifiedLoginStorageService } from '../unified-login/unified-login-storage.service';
import { UnifiedLoginEnvService } from '../unified-login/unified-login-env.service';

type TOnNotificationCallbackFn<T = unknown> = (data: T, eventType: 'received' | 'action_performed') => void;
export type TPushNotificationCallbackEventType = Parameters<TOnNotificationCallbackFn>[1];

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  #sucessCallback!: (value: TNotificationTypes | PromiseLike<TNotificationTypes>) => void;
  #registartionPromise: Promise<TNotificationTypes>;

  constructor(
    private alertController: AlertController,
    private loggedInUserStorageService: UnifiedLoginStorageService,
    private envProvider: UnifiedLoginEnvService,
  ) {
    console.log('PushNotificationService constructer called');
    const appType = this.envProvider.getAppType();
    // Push notification is only available on machine mainteance
    if (appType === 'MM_APP_SOLVEI8' || appType === 'PT_APP') {
      if (Capacitor.isNativePlatform()) {
        this.#registerEvents();
      }
    }
    this.#registartionPromise = new Promise<TNotificationTypes>((resolve) => {
      this.#sucessCallback = resolve;
    });
  }

  #initPush() {
    if (Capacitor.isNativePlatform()) {
      console.log('PushNotificationService initPush');
      this.#registerNotifications();
    } else {
      this.#sucessCallback({ type: 'Web' });
      console.log('[ERROR][PUSH_NOTIFICATION] Push notification');
    }
  }

  registerDevice() {
    console.log('PushNotificationService registerDevice');
    this.#initPush();
    return this.#registartionPromise;
  }

  getDeviceToken() {
    const appType = this.envProvider.getAppType();
    // Push notification is only available on machine maintenance and plani8
    if (appType !== 'MM_APP_SOLVEI8' && appType !== 'PT_APP') return Promise.resolve(null);

    return this.loggedInUserStorageService
      .get('[LOGGED_IN_USER][NOTIFICATION_TOKEN]')
      .then((token) => {
        console.log('PushNotificationService getdeviceToken called: ', token);
        return token;
      })
      .catch((error) => {
        console.log('PushNotificationService getdeviceToken error: ', error);
        return null;
      });
  }

  onNotification<T>(callback: TOnNotificationCallbackFn<T>) {
    if (Capacitor.isNativePlatform()) {
      // Handle Local Notification
      this.#onAppNotificationRecieved(callback);
      // Handle App Notification
      this.#onAppNotificationActionPerformed(callback);
    } else {
      console.log('[ERROR][PUSH_NOTIFICATION] Push notification not available for web');
    }
  }

  async registerNotificationChannel(notifChannelData: TPushNotificationChannel) {
    await PushNotifications.createChannel(notifChannelData);
  }

  async #onAppNotificationRecieved<T = unknown>(callback: TOnNotificationCallbackFn<T>) {
    await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
      const allNotification = await PushNotifications.getDeliveredNotifications();
      const filterLocalNotification = allNotification.notifications.filter((v) => !v.tag);
      await PushNotifications.removeDeliveredNotifications({
        notifications: filterLocalNotification,
      });
      const alert = this.alertController.create({
        header: notification.title,
        message: notification.body,
        backdropDismiss: false,
        buttons: [
          'Close',
          {
            text: 'Check',
            handler: async () => {
              callback(notification.data as T, 'received');
            },
          },
        ],
      });

      alert.then((a) => a.present());
    });
  }

  async #onAppNotificationActionPerformed<T = unknown>(callback: TOnNotificationCallbackFn<T>) {
    await PushNotifications.addListener('pushNotificationActionPerformed', (actionPerformed) => {
      callback(actionPerformed.notification.data as T, 'action_performed');
    });
  }

  async #registerEvents() {
    console.log('PushNotifications registerEvents called');
    await PushNotifications.removeAllListeners();
    await PushNotifications.addListener('registration', (token) => {
      console.log('Registration token: ', token.value);
      this.loggedInUserStorageService.set('[LOGGED_IN_USER][NOTIFICATION_TOKEN]', token.value);
      this.#sucessCallback({ type: 'App', deviceToken: token.value });
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration error: ', err.error);
      this.#sucessCallback({ type: 'Error', error: err.error });
    });
  }

  async #registerNotifications() {
    console.log('Push Notification registerNotifications called');
    console.log('Push Notification checkPermissions called');
    let permStatus = await PushNotifications.checkPermissions();
    console.log('Push Notification permStatus:', permStatus);
    if (permStatus.receive === 'prompt') {
      console.log('Push Notification Permission prompt');
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      console.log('Push Notification Permission denied');
    }
    console.log('Push Notification register called');
    await PushNotifications.register();
  }
}

// Notificatino Success Types
type TWeb_Notification = {
  type: 'Web';
};

type TApp_Notification = {
  type: 'App';
  deviceToken: string;
};

type TError_Notification = {
  type: 'Error';
  error: string;
};

type TNotificationTypes = TWeb_Notification | TApp_Notification | TError_Notification;

// Notification Channel For Android
export type TPushNotificationChannel = {
  /**
   * The channel identifier.
   */
  id: string;
  /**
   * The human-friendly name of this channel (presented to the user).
   */
  name: string;
  /**
   * The description of this channel (presented to the user).
   */
  description?: string;
  /**
   * The sound that should be played for notifications posted to this channel.
   * The file name of a sound file should be specified relative to the android
   * app `res/raw` directory.
   * @example "jingle.wav"
   */
  sound?: string;
  /**
   * Whether notifications posted to this channel should vibrate.
   */
  vibration?: boolean;
};
